/**
 * Commissioning Panel — model approval pipeline queue with license compliance.
 * Phase 4.7 — WQ-166
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon } from '../common/Icons';
import {
  listCommissions, startCommission, approveCommission, rejectCommission,
  retryCommission, runDiscovery, type Commission,
} from '../../services/commissioningService';

const SOURCE_COLORS: Record<string, string> = {
  huggingface: '#eab308', ollama: '#22c55e', manual: '#6b7280', api: '#3b82f6',
};

const COMMISSION_STATUS_COLORS: Record<string, string> = {
  discovered: '#6b7280', validating: '#3b82f6', license_review: '#eab308',
  staging: '#ea580c', approved: '#22c55e', rejected: '#dc2626',
};

const LICENSE_STATUS_COLORS: Record<string, string> = {
  approved: '#22c55e', flagged: '#eab308', rejected: '#dc2626', unknown: '#6b7280',
};

const ORIGIN_STATUS_COLORS: Record<string, string> = {
  approved: '#22c55e', blocked: '#dc2626', unverified: '#6b7280',
};

const Badge: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <span style={{
    fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
    background: `${color}20`, color,
  }}>{label}</span>
);

const CommissioningPanel: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [createForm, setCreateForm] = useState({ source: 'manual', source_model_id: '', origin_country: '', license_id: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { status?: string; source?: string } = {};
      if (statusFilter) filters.status = statusFilter;
      if (sourceFilter) filters.source = sourceFilter;
      const data = await listCommissions(authFetch, filters);
      setCommissions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load commissions');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading, statusFilter, sourceFilter]);

  const handleApprove = async (id: string) => {
    setActionLoading(true);
    try {
      await approveCommission(authFetch, id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!showRejectModal || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await rejectCommission(authFetch, showRejectModal, rejectReason.trim());
      setShowRejectModal(null);
      setRejectReason('');
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed');
    }
    setActionLoading(false);
  };

  const handleRetry = async (id: string) => {
    setActionLoading(true);
    try {
      await retryCommission(authFetch, id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Retry failed');
    }
    setActionLoading(false);
  };

  const handleCreate = async () => {
    if (!createForm.source_model_id.trim()) return;
    setActionLoading(true);
    try {
      await startCommission(authFetch, {
        source: createForm.source,
        source_model_id: createForm.source_model_id.trim(),
        ...(createForm.origin_country ? { origin_country: createForm.origin_country } : {}),
        ...(createForm.license_id ? { license_id: createForm.license_id } : {}),
      });
      setShowCreateModal(false);
      setCreateForm({ source: 'manual', source_model_id: '', origin_country: '', license_id: '' });
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
    setActionLoading(false);
  };

  const handleDiscovery = async () => {
    setActionLoading(true);
    try {
      await runDiscovery(authFetch);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Discovery failed');
    }
    setActionLoading(false);
  };

  const relativeTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (loading && commissions.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading commissions...</div>;
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}>
          <option value="">All Statuses</option>
          <option value="discovered">Discovered</option>
          <option value="validating">Validating</option>
          <option value="license_review">License Review</option>
          <option value="staging">Staging</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}>
          <option value="">All Sources</option>
          <option value="huggingface">HuggingFace</option>
          <option value="ollama">Ollama</option>
          <option value="manual">Manual</option>
          <option value="api">API</option>
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={handleDiscovery} disabled={actionLoading} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <SearchIcon size={14} /> Run Discovery
        </button>
        <button onClick={() => setShowCreateModal(true)} style={{
          fontSize: 13, padding: '6px 16px', borderRadius: 6,
          border: 'none', fontWeight: 500, cursor: 'pointer',
          background: 'var(--accent-primary, #3b82f6)', color: '#fff',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          + Start Commission
        </button>
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Pipeline Table */}
      {commissions.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No model commissions started. Click &quot;Start Commission&quot; to begin the model approval pipeline.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Source</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Model ID</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>License</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Origin</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>By</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Date</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {commissions.map(c => (
                <React.Fragment key={c.id}>
                  <tr style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: expandedId === c.id ? 'var(--bg-secondary)' : 'transparent' }}
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge label={c.source} color={SOURCE_COLORS[c.source] || '#888'} />
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={c.source_model_id}>
                      {c.source_model_id}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <Badge label={c.commission_status} color={COMMISSION_STATUS_COLORS[c.commission_status] || '#888'} />
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <Badge label={c.license_status} color={LICENSE_STATUS_COLORS[c.license_status] || '#888'} />
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <Badge label={c.origin_status} color={ORIGIN_STATUS_COLORS[c.origin_status] || '#888'} />
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>{c.commissioned_by}</td>
                    <td style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                      {relativeTime(c.created_at)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        {(c.commission_status === 'staging' || c.commission_status === 'license_review') && (
                          <button onClick={() => handleApprove(c.id)} disabled={actionLoading}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #22c55e', background: '#22c55e20', color: '#22c55e', cursor: 'pointer' }}>
                            Approve
                          </button>
                        )}
                        {c.commission_status !== 'approved' && c.commission_status !== 'rejected' && (
                          <button onClick={() => { setShowRejectModal(c.id); setRejectReason(''); }} disabled={actionLoading}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #dc2626', background: '#dc262620', color: '#dc2626', cursor: 'pointer' }}>
                            Reject
                          </button>
                        )}
                        {c.commission_status === 'rejected' && (
                          <button onClick={() => handleRetry(c.id)} disabled={actionLoading}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #3b82f6', background: '#3b82f620', color: '#3b82f6', cursor: 'pointer' }}>
                            Retry
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Detail Row */}
                  {expandedId === c.id && (
                    <tr>
                      <td colSpan={8} style={{ padding: '12px 24px', background: 'var(--bg-secondary)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 13 }}>
                          <div><strong>ID:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{c.id}</span></div>
                          <div><strong>Model ID:</strong> {c.model_id || 'N/A'}</div>
                          <div><strong>Reviewed By:</strong> {c.reviewed_by || 'N/A'}</div>
                          <div><strong>Origin Country:</strong> {c.origin_country || 'N/A'}</div>
                          {c.rejection_reason && (
                            <div style={{ gridColumn: '1/-1' }}><strong>Rejection Reason:</strong> {c.rejection_reason}</div>
                          )}
                        </div>
                        {c.source_metadata && Object.keys(c.source_metadata).length > 0 && (
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Source Metadata</summary>
                            <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 200, padding: 8, background: 'var(--bg-tertiary)', borderRadius: 6 }}>
                              {JSON.stringify(c.source_metadata, null, 2)}
                            </pre>
                          </details>
                        )}
                        {c.license_details && Object.keys(c.license_details).length > 0 && (
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>License Details</summary>
                            <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 200, padding: 8, background: 'var(--bg-tertiary)', borderRadius: 6 }}>
                              {JSON.stringify(c.license_details, null, 2)}
                            </pre>
                          </details>
                        )}
                        {c.benchmark_results && Object.keys(c.benchmark_results).length > 0 && (
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Benchmark Results</summary>
                            <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 200, padding: 8, background: 'var(--bg-tertiary)', borderRadius: 6 }}>
                              {JSON.stringify(c.benchmark_results, null, 2)}
                            </pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* License Compliance Legend */}
      <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 12 }}>
        <strong style={{ fontSize: 13 }}>License Compliance</strong>
        <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
          <span><Badge label="Approved" color="#22c55e" /> MIT, Apache 2.0, BSD, ISC, Unlicense, CC0, Zlib</span>
          <span><Badge label="Flagged" color="#eab308" /> CC-BY-4.0, proprietary, unknown, custom</span>
          <span><Badge label="Rejected" color="#dc2626" /> GPL, AGPL, LGPL, MPL, EPL, CC-BY-SA, CDDL, OSL, CC-BY-NC</span>
        </div>
      </div>

      {/* Create Commission Modal */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowCreateModal(false)}>
          <div className="glass-card" style={{ padding: 24, minWidth: 400, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>Start Commission</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Source
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {['manual', 'huggingface', 'ollama', 'api'].map(s => (
                    <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" name="source" value={s} checked={createForm.source === s}
                        onChange={() => setCreateForm(f => ({ ...f, source: s }))} />
                      {s}
                    </label>
                  ))}
                </div>
              </label>
              <label style={{ fontSize: 13 }}>
                Source Model ID *
                <input type="text" value={createForm.source_model_id}
                  onChange={e => setCreateForm(f => ({ ...f, source_model_id: e.target.value }))}
                  placeholder="e.g. meta-llama/Llama-3-8B"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 13 }}>
                Origin Country (optional)
                <input type="text" value={createForm.origin_country}
                  onChange={e => setCreateForm(f => ({ ...f, origin_country: e.target.value }))}
                  placeholder="e.g. US"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 13 }}>
                License ID (optional)
                <input type="text" value={createForm.license_id}
                  onChange={e => setCreateForm(f => ({ ...f, license_id: e.target.value }))}
                  placeholder="e.g. apache-2.0"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => setShowCreateModal(false)} style={{
                  fontSize: 13, padding: '6px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                }}>Cancel</button>
                <button onClick={handleCreate} disabled={actionLoading || !createForm.source_model_id.trim()} style={{
                  fontSize: 13, padding: '6px 16px', borderRadius: 6,
                  border: 'none', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--accent-primary, #3b82f6)', color: '#fff',
                }}>
                  Start Commission
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowRejectModal(null)}>
          <div className="glass-card" style={{ padding: 24, minWidth: 400, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>Reject Commission</h3>
            <label style={{ fontSize: 13 }}>
              Rejection Reason *
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                rows={3} placeholder="Explain why this commission is being rejected..."
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4, resize: 'vertical' }} />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setShowRejectModal(null)} style={{
                fontSize: 13, padding: '6px 12px', borderRadius: 6,
                border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
                background: 'var(--bg-secondary)', color: 'var(--text-primary)',
              }}>Cancel</button>
              <button onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}
                style={{ fontSize: 13, padding: '6px 16px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissioningPanel;
